import { ItemWithLabel, palette, TextIndicator } from '@innovyze/stylovyze';
import { Divider, Link, Typography } from '@mui/material';
import { CheckCircle, CloseRounded } from '@mui/icons-material';
import { useField } from 'formik';
import React from 'react';
import Alert from '@mui/lab/Alert';

import { useGlobalization } from '@Translations/useGlobalization';
import {
	IntegrationInfo,
	IntegrationStatus,
} from '../../types/integration.types';
import {
	IntegrationInfoBody,
	IntegrationInfoContainer,
	IntegrationInfoHeader,
	IntegrationInfoTable,
} from './VaparSettings.styles';
import { useParams } from 'react-router-dom';
import VaparAuthenticateButton from './VaparAuthenticateButton';
import { StatusItemWithLabel } from '../arcgis/ArcGisSettings/ItemsWithLabel/StatusItemWithLabel';
import { IntegrationsTypeWithLabel } from '../arcgis/ArcGisSettings/ItemsWithLabel/IntegrationTypeWithLabel';

export interface IntegrationInfoDisplayProps {
	setVaparAuthOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function IntegrationInfoDisplay({
	setVaparAuthOpen,
}: IntegrationInfoDisplayProps) {
	const [{ value }, ,] = useField<IntegrationInfo>('integrationInfo');
	const { t } = useGlobalization();
	const { editId } = useParams<{ editId: string }>();

	const isEditMode = editId != null;
	const isValid = value.isValid;
	const showReAuth = isEditMode && !isValid;

	return (
		<IntegrationInfoContainer>
			<IntegrationInfoHeader>
				<Link onClick={() => setVaparAuthOpen(true)}>
					<Typography variant="body2">
						{t('Logged in as {{username}}', {
							username: value?.username,
							context: 'ArcGis',
						})}
					</Typography>
				</Link>
				{value.authenticated ? (
					<TextIndicator
						icon={<CheckCircle color={'success'} />}
						label={t('Authenticated')}
						variant="outlined"
						textColor={palette.success.dark}
						iconColor={palette.success.dark}
					/>
				) : (
					<TextIndicator
						icon={<CloseRounded />}
						label={t('Not Authenticated')}
						variant="outlined"
						textColor={palette.error.dark}
						iconColor={palette.error.dark}
					/>
				)}
			</IntegrationInfoHeader>
			<Divider variant="fullWidth" />
			{showReAuth && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
					}}>
					<Alert
						variant="filled"
						style={{
							width: '100%',
							display: 'grid',
							justifyContent: 'space-between',
							alignItems: 'center',
							gridTemplateColumns: 'auto 1fr',
						}}
						severity="warning">
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: '1fr auto',
								justifyContent: 'space-between',
								alignItems: 'center',
								textAlign: 'center',
							}}>
							{t(
								'Authentication Expired, please re-authenticate',
							)}
							<VaparAuthenticateButton
								setVaparAuthOpen={setVaparAuthOpen}
							/>
						</div>
					</Alert>
				</div>
			)}

			<IntegrationInfoBody>
				<IntegrationInfoTable>
					<StatusItemWithLabel
						status={
							value.isEnabled
								? IntegrationStatus.Enabled
								: IntegrationStatus.Disabled
						}
					/>
					<ItemWithLabel
						label={t('Videos Processed')}
						value={value.videosProcessed}
					/>
					<IntegrationsTypeWithLabel
						integrationType={value.integrationType}
					/>
					<ItemWithLabel
						label={t('Last Used Time')}
						value={
							value.lastUpdatedData
								? new Date(
										value.lastUpdatedData,
								  ).toLocaleString()
								: '--'
						}
					/>
				</IntegrationInfoTable>
			</IntegrationInfoBody>
		</IntegrationInfoContainer>
	);
}
